import Base from '@studiometa/js-toolkit/abstracts/Base';
import transition, { setClassesOrStyles } from '@studiometa/js-toolkit/utils/css/transition';
import withBreakpointObserver from '@studiometa/js-toolkit/decorators/withBreakpointObserver';
import { isDev, MENU_BURGER_BREAKPOINTS } from '../../config';
import ItemFirstLevel from './ItemFirstLevel';

/**
 * MenuBurger class.
 */
export default class MenuBurger extends withBreakpointObserver(Base) {
  /**
   * Class config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'MenuBurger',
      log: isDev(),
      components: {
        ItemFirstLevel,
      },
      activeBreakpoints: MENU_BURGER_BREAKPOINTS,
      styles: {
        nav: {
          closed: 'transform translate-x-full pointer-events-none',
          active: 'transition duration-700 ease-out-expo',
        },
      },
    };
  }

  /**
   * Make sure the nav is closed when mounted.
   */
  mounted() {
    this.close(false);

    // Remove the hidden class from the topbar block
    if (this.$refs.topbar) {
      this.$refs.topbar.classList.remove('hidden');
    }
  }

  /**
   * Remove the styles when the menu is destroyed.
   */
  destroyed() {
    setClassesOrStyles(this.$refs.nav, this.$options.styles.nav.closed, 'remove');

    // Re-add the hidden class from the topbar block
    if (this.$refs.topbar) {
      this.$refs.topbar.classList.add('hidden');
    }
  }

  /**
   * Close the submenu when pressing the escape key.
   *
   * @param  {Object}  options
   * @param  {Boolean} options.isDown If the event is `keydown`.
   * @param  {Boolean} options.ESC    If the key pressed is the escape key.
   * @return {void}
   */
  keyed({ isDown, ESC }) {
    if (isDown && ESC) {
      this.close();
    }
  }

  /**
   * Toggle the burger menu.
   */
  onToggleClick() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the burger menu.
   */
  async open() {
    if (this.isOpen || this.isAnimating) {
      return;
    }

    this.$emit('open');

    this.isOpen = true;
    this.isAnimating = true;
    document.documentElement.style.overflow = 'hidden';

    const offset = this.$el.getBoundingClientRect();
    this.$refs.nav.style.top = `${offset.bottom}px`;

    this.$refs.iconBurger.style.display = 'none';
    this.$refs.iconClose.style.display = '';
    this.$refs.nav.classList.remove('hidden');
    this.$refs.scroller.scrollTop = 0;

    await transition(this.$refs.nav, {
      from: this.$options.styles.nav.closed,
      active: this.$options.styles.nav.active,
    });
    this.isAnimating = false;
  }

  /**
   * Close the burger menu.
   *
   * @param {Boolean} withAnimation Whether to close the menu with animation or not.
   */
  async close(withAnimation = true) {
    if (!this.isOpen || this.isAnimating) {
      return;
    }

    this.$emit('close', withAnimation);

    this.isOpen = false;
    this.isAnimating = true;
    this.$refs.iconBurger.style.display = '';
    this.$refs.iconClose.style.display = 'none';
    document.documentElement.style.overflow = '';

    await transition(
      this.$refs.nav,
      {
        active: withAnimation ? this.$options.styles.nav.active : '',
        to: this.$options.styles.nav.closed,
      },
      'keep'
    );

    this.$refs.nav.classList.add('hidden');
    this.isAnimating = false;
  }
}
